<script setup lang="ts">
/**
 * UiKitButton — кнопка HeroUI UI-kit.
 * Цвета берутся ТОЛЬКО из токенов (классы hui-*). Хардкод hex запрещён.
 * Figma: 486-54087.
 */
type HuiColor = 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger'
type HuiVariant = 'solid' | 'bordered' | 'light' | 'flat' | 'faded' | 'shadow' | 'ghost'
type HuiSize = 'sm' | 'md' | 'lg'
type HuiRadius = 'none' | 'sm' | 'md' | 'lg' | 'full'

const props = withDefaults(
  defineProps<{
    color?: HuiColor
    variant?: HuiVariant
    size?: HuiSize
    radius?: HuiRadius
    type?: 'button' | 'submit' | 'reset'
    disabled?: boolean
    loading?: boolean
    fullWidth?: boolean
    iconOnly?: boolean
  }>(),
  {
    color: 'primary',
    variant: 'solid',
    size: 'md',
    radius: 'md',
    type: 'button',
    disabled: false,
    loading: false,
    fullWidth: false,
    iconOnly: false,
  },
)

defineEmits<{ (e: 'click', ev: MouseEvent): void }>()

defineOptions({ name: 'UiKitButton' })

const BASE =
  'relative inline-flex items-center justify-center box-border appearance-none select-none whitespace-nowrap font-medium subpixel-antialiased outline-none transition-[transform,background-color,border-color,color,box-shadow] duration-200 focus-visible:ring-2 focus-visible:ring-hui-focus focus-visible:ring-offset-2 focus-visible:ring-offset-hui-background active:scale-[0.97] disabled:opacity-50 disabled:pointer-events-none'

// variant → color → классы (полные литералы, иначе Tailwind вычистит их в prod)
const VARIANT: Record<HuiVariant, Record<HuiColor, string>> = {
  solid: {
    default: 'bg-hui-default text-hui-default-foreground',
    primary: 'bg-hui-primary text-hui-primary-foreground',
    secondary: 'bg-hui-secondary text-hui-secondary-foreground',
    success: 'bg-hui-success text-hui-success-foreground',
    warning: 'bg-hui-warning text-hui-warning-foreground',
    danger: 'bg-hui-danger text-hui-danger-foreground',
  },
  bordered: {
    default: 'bg-transparent border-2 border-hui-default text-hui-default-foreground',
    primary: 'bg-transparent border-2 border-hui-primary text-hui-primary',
    secondary: 'bg-transparent border-2 border-hui-secondary text-hui-secondary',
    success: 'bg-transparent border-2 border-hui-success text-hui-success',
    warning: 'bg-transparent border-2 border-hui-warning text-hui-warning',
    danger: 'bg-transparent border-2 border-hui-danger text-hui-danger',
  },
  light: {
    default: 'bg-transparent text-hui-default-foreground hover:bg-hui-default/40',
    primary: 'bg-transparent text-hui-primary hover:bg-hui-primary/20',
    secondary: 'bg-transparent text-hui-secondary hover:bg-hui-secondary/20',
    success: 'bg-transparent text-hui-success hover:bg-hui-success/20',
    warning: 'bg-transparent text-hui-warning hover:bg-hui-warning/20',
    danger: 'bg-transparent text-hui-danger hover:bg-hui-danger/20',
  },
  flat: {
    default: 'bg-hui-default/40 text-hui-default-foreground',
    primary: 'bg-hui-primary/20 text-hui-primary-700',
    secondary: 'bg-hui-secondary/20 text-hui-secondary-700',
    success: 'bg-hui-success/20 text-hui-success-700',
    warning: 'bg-hui-warning/20 text-hui-warning-700',
    danger: 'bg-hui-danger/20 text-hui-danger-700',
  },
  faded: {
    default: 'border-2 border-hui-default-200 bg-hui-default-100 text-hui-default-foreground',
    primary: 'border-2 border-hui-default-200 bg-hui-default-100 text-hui-primary',
    secondary: 'border-2 border-hui-default-200 bg-hui-default-100 text-hui-secondary',
    success: 'border-2 border-hui-default-200 bg-hui-default-100 text-hui-success',
    warning: 'border-2 border-hui-default-200 bg-hui-default-100 text-hui-warning',
    danger: 'border-2 border-hui-default-200 bg-hui-default-100 text-hui-danger',
  },
  shadow: {
    default: 'bg-hui-default text-hui-default-foreground shadow-lg shadow-hui-default/50',
    primary: 'bg-hui-primary text-hui-primary-foreground shadow-lg shadow-hui-primary/40',
    secondary: 'bg-hui-secondary text-hui-secondary-foreground shadow-lg shadow-hui-secondary/40',
    success: 'bg-hui-success text-hui-success-foreground shadow-lg shadow-hui-success/40',
    warning: 'bg-hui-warning text-hui-warning-foreground shadow-lg shadow-hui-warning/40',
    danger: 'bg-hui-danger text-hui-danger-foreground shadow-lg shadow-hui-danger/40',
  },
  ghost: {
    default: 'bg-transparent border-2 border-hui-default text-hui-default-foreground hover:bg-hui-default',
    primary: 'bg-transparent border-2 border-hui-primary text-hui-primary hover:bg-hui-primary hover:text-hui-primary-foreground',
    secondary: 'bg-transparent border-2 border-hui-secondary text-hui-secondary hover:bg-hui-secondary hover:text-hui-secondary-foreground',
    success: 'bg-transparent border-2 border-hui-success text-hui-success hover:bg-hui-success hover:text-hui-success-foreground',
    warning: 'bg-transparent border-2 border-hui-warning text-hui-warning hover:bg-hui-warning hover:text-hui-warning-foreground',
    danger: 'bg-transparent border-2 border-hui-danger text-hui-danger hover:bg-hui-danger hover:text-hui-danger-foreground',
  },
}

const SIZE: Record<HuiSize, string> = {
  sm: 'h-8 min-w-16 px-3 text-xs gap-1.5',
  md: 'h-10 min-w-20 px-4 text-sm gap-2',
  lg: 'h-12 min-w-24 px-6 text-base gap-2.5',
}

const SIZE_ICON: Record<HuiSize, string> = {
  sm: 'h-8 w-8 min-w-0 px-0',
  md: 'h-10 w-10 min-w-0 px-0',
  lg: 'h-12 w-12 min-w-0 px-0',
}

const RADIUS: Record<HuiRadius, string> = {
  none: 'rounded-none',
  sm: 'rounded-hui-sm',
  md: 'rounded-hui-md',
  lg: 'rounded-hui-lg',
  full: 'rounded-full',
}

const classes = computed(() => [
  BASE,
  VARIANT[props.variant][props.color],
  props.iconOnly ? SIZE_ICON[props.size] : SIZE[props.size],
  RADIUS[props.radius],
  props.fullWidth ? 'w-full' : '',
])
</script>

<template>
  <button
    :type="type"
    :class="classes"
    :disabled="disabled || loading"
    :aria-busy="loading"
    @click="$emit('click', $event)"
  >
    <svg
      v-if="loading"
      class="animate-spin h-[1em] w-[1em] shrink-0"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" />
      <path class="opacity-90" fill="currentColor" d="M12 2a10 10 0 0 1 10 10h-3a7 7 0 0 0-7-7V2z" />
    </svg>

    <slot v-if="!loading" name="leading" />
    <slot />
    <slot v-if="!loading" name="trailing" />
  </button>
</template>
